import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { isEmpty } from 'validate.js';

import { get } from 'dpl/shared/utils/object';
import { singleBreederProfileResourceDefinition } from 'dpl/shared/utils/tptConnectResourceDefinitions';
import BreederProfileApplyButton from 'dpl/components/BreederProfile/Show/BreederProfileApplyButton';
import useResourceQuery from 'dpl/shared/fetching/hooks/useResourceQuery';
import useCurrentUser from 'dpl/common/hooks/useCurrentUser';
import { ANALYTICS_VIEWS } from 'dpl/constants/analytics';
import useBreederProfileTabs from 'dpl/components/BreederProfile/hooks/useBreederProfileTabs';
import {
  BREEDER_REF,
  MOBILE_LAYOUT,
  BREED_ID
} from 'dpl/constants/query_params';
import { addToQuery } from 'dpl/util/queryString';
import useBreederProfileMobileLayout from 'dpl/components/BreederProfile/hooks/useBreederProfileMobileLayout';
import { MOBILE_LAYOUTS } from 'dpl/components/BreederProfile/utils/constants';

const CTA_LABELS = {
  REQUEST_INFO: 'Request info',
  APPLY: 'Apply'
};

interface BreederProfileApplyButtonContainerProps {
  analytics?: Record<string, any> | null;
  onLoad?: ((params: { rendered: boolean }) => void) | null;
  [key: string]: any;
}

export default function BreederProfileApplyButtonContainer({
  onLoad = null,
  analytics = null,
  ...props
}: BreederProfileApplyButtonContainerProps) {
  const { viewFeatureLocation } = useBreederProfileTabs();
  const { isNewMobileLayout } = useBreederProfileMobileLayout();
  const currentUser = useCurrentUser();

  const { breederProfileId, breederPreferredStatus } = useSelector(
    ({ server }: any) => server
  );

  const isBreederReferral = useSelector(
    ({ queryParams }: any) => !isEmpty(queryParams[BREEDER_REF])
  );

  const breedId = useSelector(({ queryParams }: any) => queryParams[BREED_ID]);

  const {
    isFetching,
    isSuccess,
    value: breederProfileShowData
  } = useResourceQuery({
    ...singleBreederProfileResourceDefinition(parseInt(breederProfileId, 10)),
    enabled: Boolean(breederProfileId)
  });

  const customCopy = isBreederReferral
    ? CTA_LABELS.APPLY
    : CTA_LABELS.REQUEST_INFO;

  const unsnoozedBreeds = breederProfileShowData.breeds.filter(
    (b: any) => !b.snoozed
  );

  const appliedTo = get(
    currentUser,
    'value.data.breeder_profiles_applied_to',
    []
  );
  const canApply =
    typeof currentUser.isLoggedIn === 'boolean' &&
    !appliedTo.includes(parseInt(breederProfileId, 10));

  const shouldShow = isSuccess && unsnoozedBreeds.length > 0 && canApply;

  function onClick() {
    const params = addToQuery({
      view: ANALYTICS_VIEWS.BREEDER_PROFILE,
      view_feature_location:
        analytics?.view_feature_location || viewFeatureLocation,
      origin_view_feature:
        analytics?.view_feature_location || viewFeatureLocation,
      ...(isNewMobileLayout && { [MOBILE_LAYOUT]: MOBILE_LAYOUTS.NEW }),
      [BREED_ID]: breedId
    });

    window.location.href = `${breederProfileShowData.profile_url}/contacts/buyer_application?${params}`;
  }

  useEffect(() => {
    if (!isFetching) {
      onLoad?.({ rendered: shouldShow });
    }
  }, [isFetching, shouldShow]);

  if (isFetching || !shouldShow) {
    return null;
  }

  return (
    <BreederProfileApplyButton
      {...props}
      analytics={analytics}
      breederProfileId={parseInt(breederProfileId, 10)}
      breederPreferredStatus={breederPreferredStatus}
      currentUser={currentUser}
      isLoggedIn={currentUser.isLoggedIn}
      onClick={onClick}
      customCopy={customCopy}
    />
  );
}
